import { motion } from "framer-motion";
import { fileUrl, videoEmbed } from "@/lib/api";
import { MAP_EMBED } from "@/lib/content";
import { Loader2 } from "lucide-react";

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({ eyebrow, title, sub, testid, className = "" }) => (
  <div className={`max-w-3xl ${className}`} data-testid={testid}>
    {eyebrow && <p className="eyebrow mb-4 flex items-center gap-3"><span className="h-px w-10 bg-gold" />{eyebrow}</p>}
    <h2 className="h-display text-4xl sm:text-5xl lg:text-6xl">{title}</h2>
    {sub && <p className="mt-5 text-sm sm:text-base text-slate-300 leading-relaxed max-w-2xl">{sub}</p>}
  </div>
);

export const PalmSilhouette = ({ className = "" }) => (
  <svg viewBox="0 0 200 300" fill="currentColor" className={className} aria-hidden="true">
    <path d="M100 300 C104 240 110 180 105 122 L111 121 C118 180 114 240 109 300 Z" />
    <path d="M108 118 C80 95 45 92 8 112 C45 101 76 108 107 124Z" />
    <path d="M108 118 C131 92 166 88 196 105 C162 98 135 108 109 125Z" />
    <path d="M108 118 C96 80 70 60 34 54 C68 68 90 90 105 123Z" />
    <path d="M108 118 C123 78 149 58 182 51 C151 68 127 92 111 123Z" />
    <path d="M108 118 C105 84 109 54 121 28 C113 60 113 90 111 121Z" />
    <path d="M108 118 C85 110 60 126 40 152 C62 131 85 121 106 125Z" />
    <path d="M108 118 C131 112 156 128 173 153 C151 133 129 123 109 125Z" />
  </svg>
);

export const PageHero = ({ eyebrow, title, sub, image, testid, children }) => (
  <section className="relative overflow-hidden grain pt-36 pb-16 sm:pt-44 sm:pb-24 bg-navy" data-testid={testid}>
    {image && <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30 animate-slowzoom" />}
    <div className="absolute inset-0 bg-gradient-to-r from-pitch via-navy/90 to-navy/40" />
    <div className="absolute inset-0 yard-lines" />
    <PalmSilhouette className="absolute -right-6 bottom-0 h-[85%] text-pitch/50" />
    <div className="relative wrap">
      {eyebrow && <p className="eyebrow mb-5">{eyebrow}</p>}
      <motion.h1
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="h-display text-5xl sm:text-6xl lg:text-7xl max-w-4xl"
      >
        {title}
      </motion.h1>
      {sub && <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300">{sub}</p>}
      {children}
    </div>
  </section>
);

export const EmptyState = ({ title, body, testid, children }) => (
  <div className="card-navy stripes p-8 sm:p-10" data-testid={testid}>
    <p className="font-display text-2xl font-extrabold uppercase italic">{title}</p>
    {body && <p className="mt-2 text-sm text-slate-400 max-w-xl">{body}</p>}
    {children}
  </div>
);

export const Loading = () => (
  <div className="flex items-center gap-3 py-10 text-slate-400" data-testid="loading-indicator">
    <Loader2 className="h-5 w-5 animate-spin text-gold" /> Loading…
  </div>
);

export const MapEmbed = ({ className = "h-80" }) => (
  <iframe
    title="PSLAA home field map"
    src={MAP_EMBED}
    className={`w-full border-0 grayscale-[30%] ${className}`}
    loading="lazy"
    referrerPolicy="no-referrer-when-downgrade"
    data-testid="home-field-map"
  />
);

export const VideoEmbed = ({ url, testid }) => {
  const v = videoEmbed(url);
  if (!v) {
    return (
      <a href={fileUrl(url)} target="_blank" rel="noreferrer" className="text-gold underline" data-testid={testid}>
        Watch video
      </a>
    );
  }
  return (
    <div className="relative aspect-video w-full bg-black" data-testid={testid}>
      {v.type === "iframe" ? (
        <iframe src={v.src} title="Video" className="absolute inset-0 h-full w-full" allowFullScreen allow="autoplay; encrypted-media" />
      ) : (
        <video src={v.src} controls className="absolute inset-0 h-full w-full" />
      )}
    </div>
  );
};

export const Gallery = ({ images = [], testid }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid={testid}>
    {images.map((src, i) => (
      <a key={src + i} href={fileUrl(src)} target="_blank" rel="noreferrer" className="group relative aspect-[4/3] overflow-hidden bg-ink">
        <img src={fileUrl(src)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </a>
    ))}
  </div>
);
